/**
 * アクセシビリティヘルパーユーティリティ
 * WCAG 2.1 AA準拠のためのユーティリティ関数集
 */

/**
 * カラーコントラスト比を計算
 * WCAG 2.1 基準: 通常テキスト 4.5:1以上、大きなテキスト 3:1以上
 */
export function calculateContrastRatio(color1: string, color2: string): number {
  const getLuminance = (color: string): number => {
    // HEXカラーを RGB に変換
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    // 相対輝度計算
    const sRGB = [r, g, b].map(c => {
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2];
  };

  const l1 = getLuminance(color1);
  const l2 = getLuminance(color2);

  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/**
 * コントラスト比がWCAG基準を満たしているかチェック
 */
export function checkContrastCompliance(
  foreground: string,
  background: string,
  level: 'AA' | 'AAA' = 'AA',
  textSize: 'normal' | 'large' = 'normal'
): boolean {
  const ratio = calculateContrastRatio(foreground, background);

  if (level === 'AAA') {
    return textSize === 'large' ? ratio >= 4.5 : ratio >= 7;
  }

  return textSize === 'large' ? ratio >= 3 : ratio >= 4.5;
}

/**
 * キーボードナビゲーション用のイベントハンドラ
 */
export function handleKeyboardNavigation(
  event: React.KeyboardEvent,
  handlers: {
    onEnter?: () => void;
    onSpace?: () => void;
    onEscape?: () => void;
    onArrowUp?: () => void;
    onArrowDown?: () => void;
    onArrowLeft?: () => void;
    onArrowRight?: () => void;
    onTab?: () => void;
  }
): void {
  const { key, shiftKey } = event;

  switch (key) {
    case 'Enter':
      handlers.onEnter?.();
      break;
    case ' ':
    case 'Space':
      event.preventDefault();
      handlers.onSpace?.();
      break;
    case 'Escape':
      handlers.onEscape?.();
      break;
    case 'ArrowUp':
      event.preventDefault();
      handlers.onArrowUp?.();
      break;
    case 'ArrowDown':
      event.preventDefault();
      handlers.onArrowDown?.();
      break;
    case 'ArrowLeft':
      event.preventDefault();
      handlers.onArrowLeft?.();
      break;
    case 'ArrowRight':
      event.preventDefault();
      handlers.onArrowRight?.();
      break;
    case 'Tab':
      if (!shiftKey) {
        handlers.onTab?.();
      }
      break;
  }
}

/**
 * ARIA属性生成ヘルパー
 */
export function generateAriaProps(config: {
  label?: string;
  describedBy?: string;
  labelledBy?: string;
  expanded?: boolean;
  selected?: boolean;
  pressed?: boolean;
  checked?: boolean;
  invalid?: boolean;
  required?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  live?: 'off' | 'polite' | 'assertive';
  role?: string;
}): Record<string, string | boolean> {
  const props: Record<string, string | boolean> = {};

  if (config.label) props['aria-label'] = config.label;
  if (config.describedBy) props['aria-describedby'] = config.describedBy;
  if (config.labelledBy) props['aria-labelledby'] = config.labelledBy;
  if (config.expanded !== undefined) props['aria-expanded'] = config.expanded;
  if (config.selected !== undefined) props['aria-selected'] = config.selected;
  if (config.pressed !== undefined) props['aria-pressed'] = config.pressed;
  if (config.checked !== undefined) props['aria-checked'] = config.checked;
  if (config.invalid) props['aria-invalid'] = 'true';
  if (config.required) props['aria-required'] = 'true';
  if (config.disabled) props['aria-disabled'] = 'true';
  if (config.hidden) props['aria-hidden'] = 'true';
  if (config.live) props['aria-live'] = config.live;
  if (config.role) props['role'] = config.role;

  return props;
}

/**
 * フォーカストラップの実装
 */
export function createFocusTrap(container: HTMLElement): {
  activate: () => void;
  deactivate: () => void;
} {
  let isActive = false;
  let previousActiveElement: Element | null = null;

  const focusableSelectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(', ');

  const getFocusableElements = (): HTMLElement[] => {
    return Array.from(container.querySelectorAll(focusableSelectors)) as HTMLElement[];
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isActive || event.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  return {
    activate: () => {
      if (isActive) return;

      previousActiveElement = document.activeElement;
      isActive = true;
      document.addEventListener('keydown', handleKeyDown);

      // 最初のフォーカス可能要素にフォーカス
      const focusableElements = getFocusableElements();
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    },
    deactivate: () => {
      if (!isActive) return;

      isActive = false;
      document.removeEventListener('keydown', handleKeyDown);

      // 前のフォーカス要素に戻す
      if (previousActiveElement && 'focus' in previousActiveElement) {
        (previousActiveElement as HTMLElement).focus();
      }
    },
  };
}

/**
 * スクリーンリーダー向けのアナウンス機能
 */
export function announceToScreenReader(
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // 短時間後に削除
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
}

/**
 * 視覚的に隠すがスクリーンリーダーには読み上げられるCSSクラス
 */
export const SCREEN_READER_ONLY_CLASS = 'sr-only';

/**
 * ID生成ヘルパー（アクセシビリティのための一意ID）
 */
export function generateAccessibleId(prefix: string = 'accessible'): string {
  return `${prefix}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * フォーカス可能要素の検出
 */
export function isFocusable(element: HTMLElement): boolean {
  const focusableSelectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ];

  return focusableSelectors.some(selector => element.matches(selector));
}

/**
 * 要素が現在表示されているかチェック
 */
export function isElementVisible(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  const style = getComputedStyle(element);

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    style.visibility !== 'hidden' &&
    style.display !== 'none' &&
    style.opacity !== '0'
  );
}

/**
 * アクセシビリティテスト用のチェック関数
 */
export function performAccessibilityCheck(element: HTMLElement): {
  passed: boolean;
  issues: string[];
} {
  const issues: string[] = [];

  // 基本的なチェック項目
  const tagName = element.tagName.toLowerCase();

  // ボタンのアクセシビリティチェック
  if (tagName === 'button') {
    if (!element.textContent?.trim() && !element.getAttribute('aria-label')) {
      issues.push('ボタンにラベルがありません');
    }
  }

  // 入力フィールドのアクセシビリティチェック
  if (tagName === 'input') {
    const type = element.getAttribute('type');
    if (type !== 'hidden') {
      const label = document.querySelector(`label[for="${element.id}"]`);
      const ariaLabel = element.getAttribute('aria-label');
      const ariaLabelledby = element.getAttribute('aria-labelledby');

      if (!label && !ariaLabel && !ariaLabelledby) {
        issues.push('入力フィールドにラベルがありません');
      }
    }
  }

  // 画像のalt属性チェック
  if (tagName === 'img') {
    const alt = element.getAttribute('alt');
    const role = element.getAttribute('role');

    if (alt === null && role !== 'presentation') {
      issues.push('画像にalt属性がありません');
    }
  }

  return {
    passed: issues.length === 0,
    issues,
  };
}
